import { createSlice } from "@reduxjs/toolkit";

const appReducer = createSlice({
  name: "appData",
  initialState: {
    locations: [],
    locationSearchResults: [],
    brands: [],
    brandsSearchResults: [],
    products: [],
    productsSearchResults: [],
    sellers: [],
    sellersSearchResults: [],
    tags: [],
    tagsSearchResults: [],
  },
  reducers: {
    // Setters
    setLocations: (state, action) => {
      state.locations = action.payload;
      state.locationSearchResults = action.payload;
    },
    setBrands: (state, action) => {
      state.brands = action.payload;
      state.brandsSearchResults = action.payload;
    },
    setProducts: (state, action) => {
      state.products = action.payload;
      state.productsSearchResults = action.payload;
    },
    setSellers: (state, action) => {
      state.sellers = action.payload;
      state.sellersSearchResults = action.payload;
    },
    setTags: (state, action) => {
      state.tags = action.payload;
      state.tagsSearchResults = action.payload;
    },

    // Updaters
    updateLocationReducer: (state, action) => {
      const { updatedLocation } = action.payload;
      const index = state.locations.findIndex(
        (location) => location.id === updatedLocation.id
      );
      if (index !== -1) {
        state.locations[index] = {
          ...state.locations[index],
          ...updatedLocation,
        };
        state.locationSearchResults = state.locations;
      }
    },

    // Search
    searchLocations: (state, action) => {
      const query = action.payload.toLowerCase();
      state.locationSearchResults = state.locations.filter((location) => {
        return [
          location.name,
          location.brand,
          location.country,
          location.city,
          location.zipCode,
          location.globalLocationNumber,
        ].some((field) => field?.toLowerCase().includes(query));
      });
    },
    searchBrands: (state, action) => {
      const query = action.payload.toLowerCase();
      state.brandsSearchResults = state.brands.filter((brand) => {
        return [
          brand.brandName,
          brand.status,
          brand.primaryProductCategory,
        ].some((field) => field?.toLowerCase().includes(query));
      });
    },
    searchProducts: (state, action) => {
      const query = action.payload.toLowerCase();
      state.productsSearchResults = state.products.filter((product) => {
        return [product.name, product.category].some((field) =>
          field?.toLowerCase().includes(query)
        );
      });
    },
    searchSellers: (state, action) => {
      const query = action.payload.toLowerCase();
      state.sellersSearchResults = state.sellers.filter((seller) => {
        return [seller.name, seller.email, seller.phone].some((field) =>
          field?.toLowerCase().includes(query)
        );
      });
    },
    searchTags: (state, action) => {
      const query = action.payload.toLowerCase();
      state.tagsSearchResults = state.tags.filter((tag) => {
        return [tag.brandId, tag.sellerId, tag.productId].some((field) =>
          field?.toString().toLowerCase().includes(query)
        );
      });
    },

    // Clear Search Results
    clearSearch: (state) => {
      state.locationSearchResults = state.locations;
      state.brandsSearchResults = state.brands;
      state.productsSearchResults = state.products;
      state.sellersSearchResults = state.sellers;
      state.tagsSearchResults = state.tags;
    },

    // Sort
    sortLocations: (state, action) => {
      const sortOption = action.payload;
      if (sortOption === "Alphabetical (A-Z)") {
        state.locationSearchResults.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } else if (sortOption === "Number of Spots") {
        state.locationSearchResults.sort(
          (a, b) =>
            (parseInt(a.globalLocationNumber, 10) || 0) -
            (parseInt(b.globalLocationNumber, 10) || 0)
        );
      } else if (sortOption === "Brand Name (A-Z)") {
        state.locationSearchResults.sort((a, b) =>
          a.brand.localeCompare(b.brand)
        );
      }
    },
    sortBrands: (state, action) => {
      const sortOption = action.payload;
      if (sortOption === "Alphabetical (A-Z)") {
        state.brandsSearchResults.sort((a, b) =>
          a.brandName.localeCompare(b.brandName)
        );
      } else if (sortOption === "Alphabetical (Z-A)") {
        state.brandsSearchResults.sort((a, b) =>
          b.brandName.localeCompare(a.brandName)
        );
      } else if (sortOption === "Last Modified") {
        state.brandsSearchResults.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      }
    },
    sortProducts: (state, action) => {
      const sortOption = action.payload;
      if (sortOption === "Alphabetical (A-Z)") {
        state.productsSearchResults.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } else if (sortOption === "Alphabetical (Z-A)") {
        state.productsSearchResults.sort((a, b) =>
          b.category.localeCompare(a.category)
        );
      } else if (sortOption === "Last Modified") {
        state.productsSearchResults.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      }
    },
    sortSellers: (state, action) => {
      const sortOption = action.payload;
      if (sortOption === "Alphabetical (A-Z)") {
        state.sellersSearchResults.sort((a, b) => a.name.localeCompare(b.name));
      } else if (sortOption === "Alphabetical (Z-A)") {
        state.sellersSearchResults.sort((a, b) => b.name.localeCompare(a.name));
      } else if (sortOption === "Last Modified") {
        state.sellersSearchResults.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      }
    },
    sortTags: (state, action) => {
      const sortOption = action.payload;
      if (sortOption === "Brand ID (A-Z)") {
        state.tagsSearchResults.sort((a, b) =>
          a.brandId.toString().localeCompare(b.brandId)
        );
      } else if (sortOption === "Seller ID (A-Z)") {
        state.tagsSearchResults.sort((a, b) =>
          a.sellerId.toString().localeCompare(b.sellerId)
        );
      } else if (sortOption === "Product ID (A-Z)") {
        state.tagsSearchResults.sort((a, b) =>
          a.productId.toString().localeCompare(b.productId)
        );
      }
    },

    // Clear Specific Search Results
    clearLocationSearch: (state) => {
      state.locationSearchResults = state.locations;
    },
    clearBrandSearch: (state) => {
      state.brandsSearchResults = state.brands;
    },
    clearProductSearch: (state) => {
      state.productsSearchResults = state.products;
    },
    clearSellerSearch: (state) => {
      state.sellersSearchResults = state.sellers;
    },
    clearTagSearch: (state) => {
      state.tagsSearchResults = state.tags;
    },

    // Clear All Data
    clearData: () => ({
      locations: [],
      locationSearchResults: [],
      brands: [],
      brandsSearchResults: [],
      products: [],
      productsSearchResults: [],
      sellers: [],
      sellersSearchResults: [],
      tags: [],
      tagsSearchResults: [],
    }),
  },
});

export const {
  setLocations,
  setBrands,
  setProducts,
  setSellers,
  setTags,
  updateLocationReducer,
  searchLocations,
  searchBrands,
  searchProducts,
  searchSellers,
  searchTags,
  clearSearch,
  sortLocations,
  sortBrands,
  sortProducts,
  sortSellers,
  sortTags,
  clearLocationSearch,
  clearBrandSearch,
  clearProductSearch,
  clearSellerSearch,
  clearTagSearch,
  clearData,
} = appReducer.actions;

export default appReducer.reducer;

// import { createSlice } from "@reduxjs/toolkit";

// export const appReducer = createSlice({
//   name: "appData",
//   initialState: {
//     locations: [],
//     locationSearchResults: [],
//     brands: [],
//     brandsSearchResults: [],
//     products: [],
//     productsSearchResults: [],
//     sellers: [],
//     sellersSearchResults: [],
//     tags: [],
//     tagsSearchResults: [],
//   },
//   reducers: {
//     setLocations: (state, action) => {
//       const updatedState = {
//         ...state,
//         locations: action.payload,
//         locationSearchResults: action.payload,
//       };
//       return updatedState;
//     },
//     updateLocationReducer: (state, action) => {
//       const { updatedLocation } = action.payload;
//       const index = state.locations.findIndex(
//         (location) => location.id === updatedLocation.id
//       );

//       if (index !== -1) {
//         state.locations[index] = Object.assign(
//           {},
//           state.locations[index],
//           updatedLocation
//         );
//         state.locationSearchResults = state.locations;
//       }
//     },

//     setBrands: (state, action) => {
//       const updatedState = {
//         ...state,
//         brands: action.payload,
//         brandsSearchResults: action.payload,
//       };
//       return updatedState;
//     },
//     setProducts: (state, action) => {
//       const updatedState = {
//         ...state,
//         products: action.payload,
//         productsSearchResults: action.payload,
//       };
//       return updatedState;
//     },
//     setSellers: (state, action) => {
//       const updatedState = {
//         ...state,
//         sellers: action.payload,
//         sellersSearchResults: action.payload,
//       };
//       return updatedState;
//     },
//     sellersSearchResults: (state, action) => {
//       const query = action.payload.toLowerCase();
//       state.sellersSearchResults = state.sellers.filter(
//         (seller) =>
//           seller.name?.toLowerCase().includes(query) ||
//           seller.email?.toLowerCase().includes(query) ||
//           seller.phone?.toLowerCase().includes(query)
//       );
//     },
//     clearSellers: (state) => {
//       state.sellersSearchResults = state.sellers;
//     },
//     setTags: (state, action) => {
//       const updatedState = {
//         ...state,
//         tags: action.payload,
//         tagsSearchResults: action.payload,
//       };
//       return updatedState;
//     },
//     searchTags: (state, action) => {
//       const query = action.payload.toLowerCase();

//       state.tagsSearchResults = state.tags.filter(
//         (tag) =>
//           tag.brandId?.toString().toLowerCase().includes(query) ||
//           tag.sellerId?.toString().toLowerCase().includes(query) ||
//           tag.productId?.toString().toLowerCase().includes(query)
//       );
//     },

//     sortTags: (state, action) => {
//       const sortOption = action.payload;
//       if (sortOption === "Brand ID (A-Z)") {
//         // Sort by brand ID
//         state.tagsSearchResults.sort((a, b) =>
//           a.brandId.toString().localeCompare(b.brandId)
//         );
//       } else if (sortOption === "Seller ID (A-Z)") {
//         // Sort by seller ID
//         state.tagsSearchResults.sort((a, b) =>
//           a.sellerId.toString().localeCompare(b.sellerId)
//         );
//       } else if (sortOption === "Product ID (A-Z)") {
//         // Sort by product ID
//         state.tagsSearchResults.sort((a, b) =>
//           a.productId.toString().localeCompare(b.productId)
//         );
//       }
//     },
//     searchLocations: (state, action) => {
//       const query = action.payload.toLowerCase();
//       state.locationSearchResults = state.locations.filter(
//         (location) =>
//           location.name?.toLowerCase().includes(query) ||
//           location.brand?.toLowerCase().includes(query) ||
//           location.country?.toLowerCase().includes(query) ||
//           location.city?.toLowerCase().includes(query) ||
//           location.zipCode?.toLowerCase().includes(query) ||
//           location?.globalLocationNumber?.toLowerCase().includes(query)
//       );
//     },
//     clearTagsSearchResults: (state) => {
//       state.tagsSearchResults = state.tags; // Reset tagsSearchResults to the original data
//     },
//     sortLocations: (state, action) => {
//       const sortOption = action.payload;

//       if (sortOption === "Alphabetical (A-Z)") {
//         // Sort by location name alphabetically
//         state.locationSearchResults.sort((a, b) =>
//           a.name.localeCompare(b.name)
//         );
//       } else if (sortOption === "Number of Spots") {
//         // Sort by globalLocationNumber numerically (fallback to 0 if undefined)
//         state.locationSearchResults.sort(
//           (a, b) =>
//             (parseInt(a.globalLocationNumber, 10) || 0) -
//             (parseInt(b.globalLocationNumber, 10) || 0)
//         );
//       } else if (sortOption === "Brand Name (A-Z)") {
//         // Sort by brand alphabetically
//         state.locationSearchResults.sort((a, b) =>
//           a.brand.localeCompare(b.brand)
//         );
//       }
//     },

//     searchProducts: (state, action) => {
//       const query = action.payload.toLowerCase();
//       state.productsSearchResults = state.products.filter(
//         (product) =>
//           product.name?.toLowerCase().includes(query) ||
//           product.category?.toLowerCase().includes(query)
//       );
//     },

//     sortProducts: (state, action) => {
//       const sortOption = action.payload;

//       if (sortOption === "Alphabetical (A-Z)") {
//         // Sort by product name alphabetically
//         state.productsSearchResults.sort((a, b) =>
//           a.name.localeCompare(b.name)
//         );
//       } else if (sortOption === "Alphabetical (Z-A)") {
//         // Sort by category alphabetically z-a
//         state.productsSearchResults.sort((a, b) =>
//           b.category.localeCompare(a.category)
//         );
//       } else if (sortOption === "Last Modified") {
//         // Sort by last updated date
//         state.productsSearchResults.sort(
//           (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
//         );
//       }
//     },
//     searchBrands: (state, action) => {
//       const query = action.payload.toLowerCase();
//       state.brandsSearchResults = state.brands.filter(
//         (brand) =>
//           brand.brandName?.toLowerCase().includes(query) ||
//           brand.status?.toLowerCase().includes(query) ||
//           brand?.primaryProductCategory?.toLowerCase().includes(query)
//       );
//     },

//     sortBrands: (state, action) => {
//       const sortOption = action.payload;

//       if (sortOption === "Alphabetical (A-Z)") {
//         // Sort by brand name alphabetically
//         state.brandsSearchResults.sort((a, b) =>
//           a.brandName.localeCompare(b.brandName)
//         );
//       } else if (sortOption === "Alphabetical (Z-A)") {
//         // Sort by brand name reverse alphabetically
//         state.brandsSearchResults.sort((a, b) =>
//           b.brandName.localeCompare(a.brandName)
//         );
//       } else if (sortOption === "Last Modified") {
//         // Sort by updatedAt date
//         state.brandsSearchResults.sort(
//           (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
//         );
//       }
//     },

//     clearSearch: (state) => {
//       state.locationSearchResults = state.locations; // Reset locationSearchResults to the original data
//     },

//     clearBrandSearchResults: (state) => {
//       state.brandsSearchResults = state.brands;
//     },
//     clearProductsSearchResults: (state) => {
//       state.productsSearchResults = state.products; // Reset productsSearchResults to the original data
//     },

//     clearData: () => {
//       const updatedState = {
//         locations: [],
//         brands: [],
//         products: [],
//         sellers: [],
//         tags: [],
//         locationSearchResults: [],
//         productsSearchResults: [],
//         sellersSearchResults: [],
//         tagsSearchResults: [],
//       };
//       return updatedState;
//     },
//   },
// });

// export const {
//   setLocations,
//   setBrands,
//   searchBrands,
//   sortBrands,
//   clearBrandSearchResults,
//   setProducts,
//   setSellers,
//   setTags,
//   searchLocations,
//   sortLocations,
//   clearSearch,
//   clearData,
//   updateLocationReducer,
//   searchProducts,
//   sortProducts,
//   clearProductsSearchResults,
//   clearSellers,
//   sellersSearchResults,
//   searchTags,
//   sortTags,
//   clearTagsSearchResults,
// } = appReducer.actions;
// export default appReducer.reducer;
